import interact from 'interactjs';
import { dragdrop } from 'lib/drag-drop';
import { nui } from 'lib/nodes-ui';
import { ndui } from 'lib/node-drag-ui';
import { regionUI } from 'lib/region-ui';
import { appUI } from 'lib/app-ui';
import { fui } from 'lib/file-attachments-ui';
import { IllegalMapOperationError } from '../../../lib/custom_errors';
import {
  convertNewlines,
  updateSelectedNodeTree,
  updateDisplayProperties,
  refreshFreehandAncestorsOfList,
  parseHex,
  luminance,
  getStyleColorVariables,
  canPlayVideo,
  getIconNameFromExtension,
  getVideoDimensions,
  scaleRect,
  between,
  closest,
} from 'lib/util';
import CopyPaste from 'lib/copy-paste';
import { pointDistance, getPointFromAngles, point2Rect } from 'lib/geom';
import { titleYOffset, IMAGE_SIZES } from 'lib/constants';

const _reclipLink = nui.reclipLink.bind(nui);

const resizeHandlers = {};

export const setupHandleNodeResizing = ({ canvas, handleRef, nodeTag }) => {
  const node = store.getNode(nodeTag.id);
  let nodeRect;
  let containerNodeRect;
  let childNodesRect;
  let prevUserHeight;
  let prevUserWidth;
  let prevContainerUserHeight;
  let prevContainerUserWidth;
  let containerNode;
  let containerNodeTag;
  interact(handleRef)
    .draggable({
      onstart: e => {
        const { scale, tx, ty } = canvas.getMatrix();
        canvas.resizingNode = nodeTag.id;
        prevUserWidth = node.userWidth || null;
        prevUserHeight = node.userHeight || null;
        const x = (e.clientX - tx) / scale;
        const y = (e.clientY - ty) / scale;

        containerNode = node.getWidthGoverningParent();

        prevContainerUserWidth = containerNode.userWidth || null;
        prevContainerUserHeight = containerNode.userHeight || null;
        containerNodeTag = canvas.nodeTags[containerNode.id];
        nodeRect = scaleRect({rect: nodeTag.refs.container.getBoundingClientRect(), canvas });
        containerNodeRect = scaleRect({rect: containerNodeTag.refs.container.getBoundingClientRect(), canvas });
        if (containerNode.getChildrenDetached() && containerNodeTag.childNodesTag) {
          childNodesRect = scaleRect({
            rect: containerNodeTag.childNodesTag.refs.childNodes.getBoundingClientRect(),
            canvas
          });
        }
      },
      onmove: e => {
        const { scale, tx, ty } = canvas.getMatrix();
        const cursorX = (e.clientX - tx) / scale;
        const cursorY = (e.clientY - ty) / scale;
        const containerWidthDifference = containerNodeRect.width - nodeRect.width;

        const width = cursorX - nodeRect.left + containerWidthDifference;
        const height = cursorY - nodeRect.top;

        if (containerNode.getChildrenDetached() && containerNode.id !== node.id) {
          const cursorX = (e.clientX - tx) / scale;
          const cursorY = (e.clientY - ty) / scale;
          const width = cursorX - childNodesRect.left;
          containerNode.setChildrenPosition({ userWidth: width });
          containerNodeTag.setDetachedChildrenDimensions(nodeTag.id);
          containerNodeTag.childNodesTag.updateShadow();
        } else {
          containerNode.setUserDimensions({ width });
          containerNodeTag.updateContainerStyle();
        }

        node.setUserDimensions({ height });
        nodeTag.updateContainerStyle();
      },
      onend: e => {
        canvas.resizingNode = false;
        nodeTag.discoverPosition();
        nodeTag.refreshFreehandAncestors();
        const node = canvas.store.getNode(nodeTag.id);
        const messages = [];

        const nodeDimensionsMessage = canvas.operation.messages.updateNodeDimensions({
          prevUserWidth,
          prevUserHeight,
          userWidth: node.userWidth || null,
          userHeight: node.userHeight || null,
          nodeUUID: node.id,
        });

        const containerNodeDimensionsMessage = canvas.operation.messages.updateNodeDimensions({
          prevUserWidth: prevContainerUserWidth,
          prevUserHeight: prevContainerUserHeight,
          userWidth: containerNode.userWidth || null,
          userHeight: containerNode.userHeight || null,
          nodeUUID: containerNode.id,
        });

        messages.push(nodeDimensionsMessage);
        messages.push(containerNodeDimensionsMessage);

        const dragOverlaps = nui.fixOverlaps({ nodeTag, allowedDirections: ['right','down','up'] });

        if (dragOverlaps && dragOverlaps.nodePositions && dragOverlaps.nodePositions.length) {
          const nodePositionsMessage = canvas.operation.messages.updateNodePositions({
            prevNodePositions: dragOverlaps.prevNodePositions,
            nodePositions: dragOverlaps.nodePositions,
            prevLinkAngles: [],
            linkAngles: [],
            selectedNodes: dragOverlaps.nodePositions.map(n => n.uuid),
          });
          messages.push(nodePositionsMessage);
        }

        canvas.operation.batchOperation({
          messages: messages,
          onFailure: (err) => {
            console.log(`Freehand resize batchOperation failure: ${err}`);
          },
        });
      },
    });
};


const imageResizeHandlers = {};
export const setupHandleImageResizing = ({ canvas, handleRef, nodeTag }) => {
  if (imageResizeHandlers[nodeTag.id] === handleRef) return;
  imageResizeHandlers[nodeTag.id] = handleRef;

  interact(handleRef)
    .draggable({
      onstart: e => {
        canvas.clearActiveImageNode();
        if (!nodeTag) return;
        canvas.setActiveImageNode(nodeTag);

        canvas.resizeDragging = true;
        const imageTag = nodeTag.refs.image;
        const { imageWrapper } = nodeTag.refs;
        if (!imageTag) return;

        const node = canvas.store.getNode(nodeTag.id);
        nodeTag.prevDisplayProperties = Object.assign({},node.attachedFile.display_properties);

        const originalWidth = node.attachedFile.file_attributes.width;
        const originalHeight = node.attachedFile.file_attributes.height;
        const ratio = originalHeight / originalWidth ;

        const { width, height } = fui.getImageDimensions(nodeTag.id);

        const currentWidth = node.attachedFile.display_properties.width || width;
        const currentHeight = node.attachedFile.display_properties.height || height;

        nodeTag.imageRect = scaleRect({
          rect: nodeTag.refs.image.getBoundingClientRect(),
          canvas
        });

        let MIN_SIZE = IMAGE_SIZES.small;
        let minWidth;
        let minHeight;
        if (originalWidth > originalHeight) {
          minWidth = MIN_SIZE;
          minHeight = MIN_SIZE * ratio;
        } else {
          minHeight = MIN_SIZE;
          minWidth = MIN_SIZE / ratio;
        }

        nodeTag.originalImageWidth = originalWidth;
        nodeTag.originalImageHeight = originalHeight;
        nodeTag.imageRatio = ratio;
        nodeTag.prevImageWidth = currentWidth;
        nodeTag.prevImageHeight = currentHeight;
        nodeTag.minImageWidth = minWidth;
        nodeTag.minImageHeight = minHeight;

        imageTag.style.minWidth = `${minWidth}px`;
        imageTag.style.minHeight = `${minHeight}px`;
        imageTag.style.maxWidth = `${originalWidth}px`;
        imageTag.style.maxHeight = `${originalHeight}px`;
      },
      onmove: e => {
        if (e.clientX <= 0 || e.clientY <= 0) {
          return;
        }
        const imageTag = nodeTag.refs.image;
        if (!imageTag) return;

        const { scale, tx, ty } = canvas.getMatrix();
        const vpad = 10;
        const hpad = 10;
        const cursorX = (e.clientX - tx) / scale - hpad;
        const cursorY = (e.clientY - ty) / scale - vpad;
        let width = cursorX - nodeTag.imageRect.left;
        let height = cursorY - nodeTag.imageRect.top;

        let newWidth = width;
        let newHeight = height;

        if (width > height) {
          newWidth = width;
          newHeight = nodeTag.imageRatio * width;
        } else {
          newHeight = height;
          newWidth = height / nodeTag.imageRatio;
        }

        const node = canvas.store.getNode(nodeTag.id);
        const { minImageWidth, minImageHeight,originalImageWidth,originalImageHeight } = nodeTag;

        if (newWidth <= originalImageWidth && newHeight <= originalImageHeight && newWidth >= minImageWidth && newHeight >= minImageHeight) {
          nodeTag.setImageSize({ width: newWidth, height: newHeight });
          node.attachedFile.display_properties.width = newWidth;
          node.attachedFile.display_properties.height = newHeight;
          node.attachedFile.display_properties.size = 'custom';
        }
      },
      onend: e => {
        canvas.resizeDragging = false;
        canvas.clearActiveImageNode();

        const node = canvas.store.getNode(nodeTag.id);
        const messages = [];

        const fileMessage = canvas.operation.messages.updateNodeFile({
          nodeUUID: nodeTag.id,
          fileUUID: node.attachedFile.uuid,
          prevDisplayProperties: nodeTag.prevDisplayProperties,
          displayProperties: node.attachedFile.display_properties,
        });
        messages.push(fileMessage);

        const dragOverlaps = nui.fixOverlaps({ nodeTag, allowedDirections: ['right','down','up'] });

        if (dragOverlaps && dragOverlaps.nodePositions && dragOverlaps.nodePositions.length) {
          const nodePositionsMessage = canvas.operation.messages.updateNodePositions({
            prevNodePositions: dragOverlaps.prevNodePositions,
            nodePositions: dragOverlaps.nodePositions,
            prevLinkAngles: [],
            linkAngles: [],
            selectedNodes: dragOverlaps.nodePositions.map(n => n.uuid),
          });
          messages.push(nodePositionsMessage);
        }

        canvas.operation.batchOperation({
          messages: messages,
          onFailure: (err) => {
            console.log(`Freehand resize batchOperation failure: ${err}`);
          },
        });
      },
    });
};

export const setupHandleResizing = ({ canvas, handleRef, nodeTag }) => {
  if (resizeHandlers[nodeTag.id] === handleRef) return;
  resizeHandlers[nodeTag.id] = handleRef;

  interact(handleRef)
    .draggable({
      onstart: e => {
        handleRef.classList.add('resizing');
        canvas.resizeDragging = true;
        nodeTag.prevUserWidth = nodeTag.instance.childrenPosition.userWidth || 0;
        nodeTag.prevUserHeight = nodeTag.instance.childrenPosition.userHeight || 0;
        nodeTag.childNodesRect = scaleRect({
          rect: nodeTag.childNodesTag.refs.childNodes.getBoundingClientRect(),
          canvas
        });
        nodeTag.fitChildNodeContentsAndUpdate(nodeTag.instance.id);
        const ancestors = canvas.store.getNode(nodeTag.id).ancestors();
        for (const ancestor of ancestors) {
          if (ancestor.getLayoutName() == 'layout-freehand') {
            canvas.nodeTags[ancestor.id].fitChildNodeContentsAndUpdate(ancestor.id);
          }
        }
      },
      onmove: e => {
        if (e.clientX <= 0 || e.clientY <= 0) {
          // bail if we have bogus coords occasionally on the last drag event
          return;
        }
        const vpad = 0;
        const hpad = 0;
        const { scale, tx, ty } = canvas.getMatrix();
        const cursorX = (e.clientX - tx) / scale - hpad;
        const cursorY = (e.clientY - ty) / scale - vpad;
        const width = cursorX - nodeTag.childNodesRect.left;
        const height = cursorY - nodeTag.childNodesRect.top;
        nodeTag.instance.setChildrenPosition({ userWidth: width, userHeight: height });

        if (nodeTag.instance.getLayoutName() == 'layout-freehand') {
          nodeTag.instance.setUserDimensions({ width });
          nodeTag.instance.setUserDimensions({ height });
          nodeTag.fitChildNodeContentsAndUpdate(nodeTag.id);
        }

        if (nodeTag.instance.getLayoutName() != 'layout-freehand') {
          nodeTag.setDetachedChildrenDimensions();
        }
        const ancestors = canvas.store.getNode(nodeTag.id).ancestors();
        for (const ancestor of ancestors) {
          if (ancestor.getLayoutName() == 'layout-freehand') {
            canvas.nodeTags[ancestor.id].fitChildNodeContentsAndUpdate(ancestor.id);
          }
        }
        nodeTag.childNodesTag.updateShadow();
        nodeTag.topLevelParentTag().rediscoverLinkPositions();
      },
      onend: e => {
        canvas.resizeDragging = false;
        handleRef.classList.remove('resizing');

        const dragOverlaps = nui.fixOverlaps({ nodeTag, allowedDirections: ['right','down','up'] });
        const messages = [];

        if (dragOverlaps && dragOverlaps.nodePositions && dragOverlaps.nodePositions.length) {
          const nodePositionsMessage = nodeTag.operation.messages.updateNodePositions({
            prevNodePositions: dragOverlaps.prevNodePositions,
            nodePositions: dragOverlaps.nodePositions,
            prevLinkAngles: [],
            linkAngles: [],
            selectedNodes: dragOverlaps.nodePositions.map(n => n.uuid),
          });
          messages.push(nodePositionsMessage);
        }
        const childPositionsMessage = canvas.operation.messages.setChildrenPosition({
          x: nodeTag.instance.childrenPosition.x,
          y: nodeTag.instance.childrenPosition.y,
          userWidth: nodeTag.instance.childrenPosition.userWidth,
          userHeight: nodeTag.instance.childrenPosition.userHeight,
          prevUserWidth: nodeTag.prevUserWidth,
          prevUserHeight: nodeTag.prevUserHeight,
          prevX: nodeTag.instance.childrenPosition.x,
          prevY: nodeTag.instance.childrenPosition.y,
          nodeUUID: nodeTag.instance.id,
        });

        messages.push(childPositionsMessage);

        canvas.operation.batchOperation({
          messages: messages,
          onFailure: (err) => {
            console.log(`Freehand resize batchOperation failure: ${err}`);
          },
        });
      },
    });
};


export const setupNodeDragging = ({ canvas, nodeTag }) => {
  let preventAdoption = false;
  let prevNodePositions = [];
  let prevLinkAngles = [];
  let targetNodeIds = [];
  let targetLinkIds = [];
  let targetLinks = [];
  let selectedNodes = [];
  let draggingRectClientWidth = 0;
  let dragInstance;
  let dragNodeTag;
  let dragStartOperations = [];
  let draggingRect = {};
  let adoptedCardCount = 0;
  let selectedElementIds;
  let topSelectedElements;
  let movingPartsInFreehand;
  let movingTopLevelCards;
  let selectedElements = [];
  let childNodesRect;
  let firstMoveDirection;
  const linkThirdPoints = {};
  const linkThirdPointPercentages = {};
  const linkThirdPointPerpendicularDistances = {};

  interact(nodeTag.refs.container)
    .actionChecker(function (pointer, event, action) {
      if (event.target.classList.contains('resize-handle')) return null;
      // if ctrl is held down, don't trigger drag
      if (event.ctrlKey) {
        return null;
      }
      // don't trigger drag on right click
      if (event.button !== 0) {
        return null;
      }
      return action;
    })
    .styleCursor(false)
    .draggable({
      onstart: e => {
        if (nodeTag.canvas.isViewOnly()) { return; }

        // if we're not cloning, check the drag legality
        if (!e.altKey && !nodeTag.canvas.checkSelectionDragLegality()) return;

        firstMoveDirection = false;

        nodeTag.canvas.store.setCardDraggingMode(true);

        const rect = nodeTag.refs.container.getBoundingClientRect();

        if (nodeTag.getGoverningLayoutName() == 'layout-freehand') {
          const parentNodeTag = nodeTag.canvas.nodeTags[nodeTag.instance.parentId];
          childNodesRect = scaleRect({
            rect: parentNodeTag.childNodesTag.refs.childNodes.getBoundingClientRect(),
            canvas: nodeTag.canvas
          });
        } else {
          childNodesRect = null;
        }

        draggingRect.top = rect.top;
        draggingRect.bottom = rect.bottom;
        draggingRect.left = rect.left;
        draggingRect.right = rect.right;
        draggingRect.width = rect.right - rect.left;
        draggingRect.height = rect.bottom - rect.top;
        adoptedCardCount = 0;
        dragInstance = nodeTag.instance;
        dragNodeTag = nodeTag;

        // if the user holds alt or option while dragging, then clone the selection
        if (e.altKey) {
          let tempSelection = false;
          if (!nodeTag.canvas.isSelectedElement(nodeTag.id)) {
            // need to select elements in order to clone them, but don't leave the user with a selected/expanded card if it wasn't already selected
            nodeTag.canvas.setSelectedElement(nodeTag,true);
            tempSelection = true;
          }
          // if any parents of the dragged node are selected, deselect them when cloning if dragging a child
          nui.deselectParentsOf(dragInstance);

          const { nodeOldtoNewIds, linkOldtoNewIds, operationMessages} =
          CopyPaste.cloneSelectionInPlace({
            canvas: nodeTag.canvas
          });


          // run the clone operations at drag end so that clone and move can be undone at once
          dragStartOperations = operationMessages;
          const newNodeId = nodeOldtoNewIds[nodeTag.id];
          dragInstance = nodeTag.canvas.store.getNode(newNodeId);
          dragNodeTag = nodeTag.canvas.nodeTags[newNodeId];

          _reclipLink(draggingRect, dragNodeTag);

          const _selected = nodeTag.canvas.getSelectedElements();
          // if children of selected nodes are selected, deselect them so they can be moved
          for (const nodeInstance of _selected.nodes) {
            nui.deselectChildrenOf(nodeInstance);
          }

          if (tempSelection && _selected.nodes.length <= 1) {
            nodeTag.canvas.clearSelectedElement(dragNodeTag);
          }
        } else {
          if (!nodeTag.canvas.getSelectedElementIds().includes(dragInstance.id)) {
            nodeTag.canvas.clearSelectedElements();
          }
        }
        dragNodeTag.emancipating = null;
        dragNodeTag.formerParentId = null;
        nodeTag.canvas.emancipationTriggered = null; // used by discoverPosition
        dragInstance.unsetIsNewNode();
        nodeTag.canvas.hidePopups();
        nodeTag.canvas.setDragging(dragInstance);
        nodeTag.canvas.setPendingDragDY(0);
        nodeTag.canvas.setPendingDragDX(0);
        dragdrop.clearNewHome();
        dragdrop.calculateTargetRects();

        document.activeElement.blur();


        const node = store.getNode(nodeTag.id);
        const nodeLinks = node.links();

        if (nodeLinks && nodeLinks.length) {
          for (const link of nodeLinks.filter(l => l.lineCurvature === 'elbow')) {
            const fromNode = store.getNode(link.fromNodeId);
            const toNode = store.getNode(link.toNodeId);
            const fromPoint = fromNode.getFirstVisibleAncestorPosition();
            const toPoint = toNode.getFirstVisibleAncestorPosition();

            let fromXOffset = fromNode._width / 2;
            let toXOffset = toNode._width / 2;
            const verticalOffset = 28;
            const fromVerticalOffset = fromPoint._isAncestor ? 0 : verticalOffset;
            const toVerticalOffset = toPoint._isAncestor ? 0 : verticalOffset;

            let fromCenterPoint = link._overrideFromPoint || { x: fromPoint.x + fromXOffset, y: fromPoint.y + fromVerticalOffset };
            let toCenterPoint = link._overrideToPoint || { x: toPoint.x + toXOffset, y: toPoint.y + toVerticalOffset };

            if (fromNode.ancestors().includes(toNode)) {
              toCenterPoint = { x: toPoint.x, y: toPoint.y + titleYOffset };
            }

            if (fromNode.descendants().includes(toNode)) {
              fromCenterPoint = { x: fromPoint.x, y: fromPoint.y + titleYOffset };
            }


            const relationshipCenterPoint = getPointFromAngles(fromCenterPoint, link.angleStartToR, toCenterPoint, link.angleEndToR);
            const closestFromXEdge = closest(relationshipCenterPoint.x, [fromPoint.left, fromPoint.right]);
            const closestFromYEdge = closest(relationshipCenterPoint.y, [fromPoint.top, fromPoint.bottom]);
            const closestToXEdge = closest(relationshipCenterPoint.x, [toPoint.left, toPoint.right]);
            const closestToYEdge = closest(relationshipCenterPoint.y, [toPoint.top, toPoint.bottom]);

            const xDist = closestToXEdge - closestFromXEdge;
            const yDist = closestToYEdge - closestFromYEdge;

            let xPercent = false;
            let yPercent = false;
            let xPerpDist = false;
            let yPerpDist = false;

            if (between(relationshipCenterPoint.x, closestFromXEdge, closestToXEdge)) {
              xPercent = (relationshipCenterPoint.x - closestFromXEdge) / xDist;
            } else {
              xPerpDist = relationshipCenterPoint.x - ((fromCenterPoint.x + toCenterPoint.x) / 2);
            }

            if (between(relationshipCenterPoint.y, closestFromYEdge, closestToYEdge)) {
              yPercent = (relationshipCenterPoint.y - closestFromYEdge) / yDist;
            } else {
              yPerpDist = relationshipCenterPoint.y - ((fromCenterPoint.y + toCenterPoint.y) / 2);
            }

            linkThirdPoints[link.id] = relationshipCenterPoint;
            linkThirdPointPercentages[link.id] = { xPercent, yPercent };
            linkThirdPointPerpendicularDistances[link.id] = { xPerpDist, yPerpDist };
          }
        }


        const selected = nodeTag.canvas.getSelectedElements();
        selectedNodes = selected.nodes.map(n => n.id).filter(id => id != dragNodeTag.id);
        selectedNodes.push( dragNodeTag.id );

        const _targetIds = dragNodeTag.getAllSelectedNodeAndLinkIds();
        targetNodeIds = _targetIds.targetNodeIds;
        targetLinkIds = _targetIds.targetLinkIds;
        targetLinks = targetLinkIds.map(id => store.getLink(id));
        prevNodePositions = store.getNodePositions(targetNodeIds);
        prevLinkAngles = store.getLinkAngles(targetLinkIds);
        if (dragNodeTag.getGoverningLayoutName() === 'layout-freehand') {
          nodeTag.canvas.setValidFreehandDropTargets(dragInstance.getSiblingsAndRecursiveChildren());
        }

        nui.setMovingParts( nui.computeMovingParts(store, dragInstance) );
        regionUI.prepareSelectedRegionsForMove();

        selectedElements = nodeTag.canvas.selectedElements.filter(n => (n.applyMove));
        selectedElementIds = selectedElements.map(e => e.id);
        topSelectedElements = selectedElements.filter(e => (
          !(e.link() || selectedElementIds.includes(e.instance.parentId))
        ));

        movingPartsInFreehand = topSelectedElements.every(e => e.instance.getGoverningLayoutName() === 'layout-freehand');
        movingTopLevelCards = topSelectedElements.every(e => !e.instance.parentId);

        ndui.setDragSubjects({ nodeIds: [dragNodeTag.id]});
        ndui.setDragSubjects({ nodeIds: selectedElementIds});
        window.requestAnimationFrame(nui.maybePan.bind(nui, dragNodeTag));
        nodeTag.canvas.multiSelectionIndicatorBox.hide();
        // reduce initial lag when dragging due to browser repaint
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            nodeTag.canvas.setNodeDraggingStyle();
          });
        });
      },

      onmove: e => {
        nodeTag.canvas.emancipationTriggered = null;
        if (nodeTag.canvas.isViewOnly()) { return; }
        if (!dragInstance) return;
        e.preventDefault();
        e.stopPropagation();
        dragdrop.setMouseCoords({ x: e.clientX, y: e.clientY });
        const { scale, tx, ty } = nodeTag.canvas.getMatrix();
        let dx = e.dx / scale;
        let dy = e.dy / scale;
        nodeTag.canvas.setPendingDragDY(nodeTag.canvas.pendingDragDY + dy);
        nodeTag.canvas.setPendingDragDX(nodeTag.canvas.pendingDragDX + dx);
        // document.body.classList.add('node-dragging');
        // if (dragInstance.link()) {
        //   document.body.classList.add('r-node-dragging');
        // }
        if (nodeTag.canvas.pendingDragDX > 2) {
          nodeTag.canvas.setDragDirectionX('right');
          nodeTag.canvas.setPendingDragDX(0);
        } else if (nodeTag.canvas.pendingDragDX < -2) {
          nodeTag.canvas.setDragDirectionX('left');
          nodeTag.canvas.setPendingDragDX(0);
        }
        if (nodeTag.canvas.pendingDragDY > 2) {
          nodeTag.canvas.setDragDirectionY('down');
          nodeTag.canvas.setPendingDragDY(0);
        } else if (nodeTag.canvas.pendingDragDY < -2) {
          nodeTag.canvas.setDragDirectionY('up');
          nodeTag.canvas.setPendingDragDY(0);
        }

        if (e.shiftKey && !firstMoveDirection) firstMoveDirection = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

        if (e.shiftKey) {
          if (firstMoveDirection === 'x') dy = 0;
          if (firstMoveDirection === 'y') dx = 0;
        }

        nui.shiftRect(draggingRect, dx * scale, dy * scale);

        dragNodeTag.updateLinks();
        const dragLinks = dragInstance.links();

        const appliedMoveLookup = {};

        // if there's only one selected node it must be the one we're dragging
        // (since if it wasn't we would have cleared selected elements)
        if (selectedElements.length <= 1) {
          const {x, y} = dragInstance.applyMove({ dx, dy, appliedMoveLookup, canvas });
          const dragNodeLink = dragNodeTag.link();
          if (dragNodeLink) {
            dragNodeLink.setAngles({ lNodePoint: {x, y} });
            // removed linksTag.update from here - appeared to have no effect & bad performance
          }

          let emancipatingFreehandChild = null;

          if (dragNodeTag.getGoverningLayoutName() == 'layout-freehand') {
            const dragPosition = dragNodeTag.instance.absolutePosition;
            const node = dragNodeTag.instance;
            const parentInstance = dragNodeTag.instance.getParent();

            // if the child is 80% outside horizontally or 100% outside vertically then emancipate

            emancipatingFreehandChild =
               dragPosition.x + node.width * 0.8 < childNodesRect.left
               || dragPosition.x + node.width * 0.2 > childNodesRect.right
               || dragPosition.y + node.height * 1 < childNodesRect.top
               || dragPosition.y + node.height * 0.0 > childNodesRect.bottom;
          }

          if (dragNodeTag.child && (dragNodeTag.getGoverningLayoutName() !== 'layout-freehand' || emancipatingFreehandChild)) {
            dragNodeTag.formerParentId = dragInstance.parentId;
            selectedNodes.push(nodeTag.formerParentId);

            let newLocation;
            if (dragNodeTag.instance.getTopLevelParent().link()) {
              newLocation = dragNodeTag.getRenderedPosition();
            } else {
              newLocation = {
                x: dragNodeTag.instance.absolutePosition.x,
                y: dragNodeTag.instance.absolutePosition.y
              };
            }
            dragInstance.emancipateSelf(newLocation);

            dragNodeTag.child = false;
            dragNodeTag.emancipating = true;
            nodeTag.canvas.emancipationTriggered = true;
            nodeTag.canvas.nodesTag.update();
            dragNodeTag.update({ forceUpdate: true }); // important! - makes dragging stuck to cursor... ¯\_(ツ)_/¯
            dragNodeTag.updateTopLevelNeighborsLite();
            if (dragNodeTag.formerParentId && nodeTag.canvas.nodeTags[dragNodeTag.formerParentId]) {
              nodeTag.canvas.nodeTags[dragNodeTag.formerParentId].rediscoverLinkPositions();
              nodeTag.canvas.nodeTags[dragNodeTag.formerParentId].updateTopLevelNeighborsLite();
            }

            const rect = nodeTag.canvas.nodeTags[dragNodeTag.id].refs.container.getBoundingClientRect();
            draggingRect.top = rect.top;
            draggingRect.bottom = rect.bottom;
            draggingRect.left = rect.left;
            draggingRect.right = rect.right;
            draggingRect.width = rect.left - rect.right;
            draggingRect.height = rect.bottom - rect.top;

          }


          if (dragLinks && dragLinks.length) {
            for(const link of dragLinks.filter(l => l.lineCurvature === 'elbow')) {
              const fromNode = store.getNode(link.fromNodeId);
              const toNode = store.getNode(link.toNodeId);
              const fromPoint = fromNode.getFirstVisibleAncestorPosition();
              const toPoint = toNode.getFirstVisibleAncestorPosition();

              let fromXOffset = fromNode._width / 2;
              let toXOffset = toNode._width / 2;
              const verticalOffset = 28;
              const fromVerticalOffset = fromPoint._isAncestor ? 0 : verticalOffset;
              const toVerticalOffset = toPoint._isAncestor ? 0 : verticalOffset;

              let fromCenterPoint = link._overrideFromPoint || { x: fromPoint.x + fromXOffset, y: fromPoint.y + fromVerticalOffset };
              let toCenterPoint = link._overrideToPoint || { x: toPoint.x + toXOffset, y: toPoint.y + toVerticalOffset };

              if (fromNode.ancestors().includes(toNode)) {
                toCenterPoint = { x: toPoint.x, y: toPoint.y + titleYOffset };
              }

              if (fromNode.descendants().includes(toNode)) {
                fromCenterPoint = { x: fromPoint.x, y: fromPoint.y + titleYOffset };
              }

              const linkThirdPoint = linkThirdPoints[link.id];

              const closestFromXEdge = closest(linkThirdPoint.x, [fromPoint.left, fromPoint.right]);
              const closestFromYEdge = closest(linkThirdPoint.y, [fromPoint.top, fromPoint.bottom]);
              const closestToXEdge = closest(linkThirdPoint.x, [toPoint.left, toPoint.right]);
              const closestToYEdge = closest(linkThirdPoint.y, [toPoint.top, toPoint.bottom]);

              const xDist = closestToXEdge - closestFromXEdge;
              const yDist = closestToYEdge - closestFromYEdge;

              let x, y;
              if (Number.isFinite(linkThirdPointPerpendicularDistances[link.id].xPerpDist)) {
                x = (fromCenterPoint.x + toCenterPoint.x) / 2 + linkThirdPointPerpendicularDistances[link.id].xPerpDist;
              } else {
                x = closestFromXEdge + xDist * linkThirdPointPercentages[link.id].xPercent;
              }

              if (Number.isFinite(linkThirdPointPerpendicularDistances[link.id].yPerpDist)) {
                y = (fromCenterPoint.y + toCenterPoint.y) / 2 + linkThirdPointPerpendicularDistances[link.id].yPerpDist;
              } else {
                y = closestFromYEdge + yDist * linkThirdPointPercentages[link.id].yPercent;
              }

              if (!dragdrop.hit(fromPoint, point2Rect({ x, y })) && !dragdrop.hit(toPoint, point2Rect({ x, y }))) {
                if (fromNode.getTopLevelParent() != toNode.getTopLevelParent()) {
                  const lNodePoint = { x, y };
                  link.setAngles({ lNodePoint });
                  linkThirdPoints[link.id] = { x, y };
                }
              }
            }
          }

          dragNodeTag.updateLinks();

        } else {
          /* Bulk Move */

          if (preventAdoption && !nodeTag.canvas.checkSelectionDragLegality()) {
            selectedElements.forEach(el => {
              preventAdoption = true;
              if (el.refs.container.classList.contains("selected")) {
                if (el.setUndraggable) {
                  el.setUndraggable();
                }
              }
            });
          } else if (movingTopLevelCards || movingPartsInFreehand) {
            function _updateSelection(nodes) {
              nodes.forEach(node => {
                node.applyMove({ dx, dy, appliedMoveLookup, canvas });
                nodeTag.canvas.nodeTags[node.id].updatePosition();

                dragdrop.calculateTargetRects([node.id]);
                _updateSelection(node.children());
              });
            }
            _updateSelection(topSelectedElements.map(el => el.instance));

            if (movingPartsInFreehand) {

              const { emancipatedAll } = ndui.handleBulkEmancipateFromFreehand({
                topSelectedElements,
                selectedNodes,
                childNodesRect,
                dragNodeTag,
              });

              if (emancipatedAll) {
                topSelectedElements = selectedElements.filter(e => (
                  !(e.link() || selectedElementIds.includes(e.instance.parentId))
                ));
                movingPartsInFreehand = topSelectedElements.every(e => e.instance.getGoverningLayoutName() === 'layout-freehand');
                movingTopLevelCards = topSelectedElements.every(e => !e.instance.parentId);
              }
            }

          } else {
            // loop through and discover positions before we do anything else
            const cachedPositions = [];
            topSelectedElements.forEach(element => {
              const selectedNode = store.getNode(element.id);
              cachedPositions.push({id: element.id, position: selectedNode.absolutePosition});
            });
            // compute offsets
            const parentLayout = store.getNode(topSelectedElements[0].instance.parentId).getLayoutName();
            const offsets = {};
            if (parentLayout === 'layout-row') {
              cachedPositions.sort((a, b) => a.position.y - b.position.y);
              const nodeTagIdx = cachedPositions.findIndex(e => e.id === dragNodeTag.id);
              cachedPositions.forEach(({id}, idx) => {
                offsets[id] = { x: 0, y: 10 * (idx - nodeTagIdx)};
              });
            } else if (parentLayout === 'layout-col') {
              cachedPositions.sort((a, b) => a.position.x - b.position.x);
              const nodeTagIdx = cachedPositions.findIndex(e => e.id === dragNodeTag.id);
              cachedPositions.forEach(({id}, idx) => {
                offsets[id] = { y: 0, x: 10 * (idx - nodeTagIdx)};
              });
            }
            // loop through again and do things that won't fuck with the absolute positions on the store
            topSelectedElements.forEach(element => {
              const selectedNode = store.getNode(element.id);
              selectedNodes.push(selectedNode.parentId);
              const newLocation = {
                x: selectedNode.absolutePosition.x + offsets[element.id].x,
                y: selectedNode.absolutePosition.y + offsets[element.id].y,
              };
              element.formerParentId = selectedNode.parentId;
              selectedNode.emancipateSelf(newLocation);
              element.child = false;
            });
            // loop through one last time and do the updates
            nodeTag.canvas.nodesTag.update();
            topSelectedElements.forEach(element => {
              element.update(); // important! - makes dragging stuck to cursor... ¯\_(ツ)_/¯
              element.updateTopLevelNeighborsLite();
            });
            dragNodeTag.emancipating = true;
            nodeTag.canvas.emancipationTriggered = true;
            movingTopLevelCards = topSelectedElements.every(e => !e.instance.parentId);
          }
        }

        dragNodeTag.updateNodesDisplayRecursively({ignoreRefreshFreehandAncestors: true});
        // if there are any non-freehand ancestors, update link positions under top level parent
        if (store.getNode(dragNodeTag.id).ancestors().filter(n => n.getLayoutName() !== 'layout-freehand').length) {
          // TODO: only update nodes that shifted position as a result of nodeTag drag
          dragNodeTag.topNonFreehandParentTag().rediscoverLinkPositions();
        }

        targetLinks.forEach(link => {
          if (link.linkNodeId) {
            link.placeLinkNode();
            nodeTag.canvas.nodeTags[link.linkNodeId].updatePosition();
          }
        });

        targetLinks.forEach(link => {
          nodeTag.canvas.linkTags[link.id].updatePosition();
        });

        dragNodeTag.updatePosition();

        // since we may have been recently emancipated, make sure to get our most current nodeTag's container
        const container = nodeTag.canvas.nodeTags[dragNodeTag.id].refs.container;

        nodeTag.canvas.nodeTags[dragNodeTag.id]._draggingRect = draggingRect;

        const hoveredNodeAndPlacement = dragdrop.getHoveredNodeAndPlacement({
          draggingNodeId: dragNodeTag.id,
          draggingRect,
          buffer: 20,
        });

        if (hoveredNodeAndPlacement === false) {
          nodeTag.canvas.clearHoveredNode();
          nodeTag.canvas.clearHoveredLink();
        } else {
          if (hoveredNodeAndPlacement.linkTag) {
            nodeTag.canvas.setHoveredLink(hoveredNodeAndPlacement, draggingRect);
          } else {
            nodeTag.canvas.setHoveredNode(hoveredNodeAndPlacement, draggingRect, true);
          }
        }

        _reclipLink(draggingRect, dragNodeTag);

        if (dragNodeTag.emancipating) {
          const showingDropzone = dragdrop.showingDropzone;
          const newHome = dragdrop.newHome;
          dragdrop.hideDropzone(nodeTag.canvas.refs.dropzone);
          nui.unsetTransparent({ nodeIds: selectedElementIds });
          dragdrop.calculateTargetRects();
          if (showingDropzone) {
            dragdrop.showLastDropzone();
            dragdrop.newHome = newHome;
            nui.setTransparent({ nodeIds: selectedElementIds });
            dragNodeTag.emancipating = false;
          }
          nodeTag.canvas.nodeTags[dragNodeTag.id].updatePosition();
          if (dragNodeTag.formerParentId && nodeTag.canvas.nodeTags[dragNodeTag.formerParentId]) {
            nodeTag.canvas.nodeTags[dragNodeTag.formerParentId].topLevelParentTag().rediscoverLinkPositions();
          }
        }

        if (!nodeTag.canvas.floatingRLineTag.isHidden) {
          nodeTag.canvas.floatingRLineTag.update();
        };
        regionUI.applyMoveToRegions({dx,dy});
      },
      onend: e => {
        if (nodeTag.canvas.isViewOnly()) { return; }
        if (!dragInstance) return;
        nodeTag.canvas.store.setCardDraggingMode(false);
        nodeTag.canvas.emancipationTriggered = null;
        nodeTag.canvas.setDragging(null);
        try {
          // update our link tags after we clear dragging so we ensure to
          // show the arrows and dots once we land (rendering them on drag
          // was slow).
          const movingParts = nui.getMovingParts();
          if (movingParts && movingParts.targetLinkIds) {
            movingParts.targetLinkIds.forEach(lid => {
              nodeTag.canvas.linkTags[lid].update();
            });
          }
          nui.setMovingParts(null);
          const initialDragValues = nodeTag.canvas.initialDragValues;
          const operationMessages = [];

          nui.unsetTransparent({ nodeIds: selectedElementIds });
          ndui.unsetDragSubjects({ nodeIds: [dragNodeTag.id]});
          ndui.unsetDragSubjects({ nodeIds: selectedElementIds });
          nodeTag.canvas.unsetNodeDraggingStyle();
          // document.body.classList.remove('node-dragging');
          // document.body.classList.remove('r-node-dragging');
          let targetIsLink = false;
          // check to see if it's a link (but if we are also hitting a
          // node, prefer the node



          if (!nodeTag.canvas.hoveredNode && nodeTag.canvas.hoveredLink && nodeTag.canvas.hoveredLink.angleEndToR) {
            const link = nodeTag._store.getLink(nodeTag.canvas.hoveredLink.id);
            const prevLink = Object.assign({}, link);
            if (e.shiftKey && link.isValidLinkNodeId(dragNodeTag.id)) {
              targetIsLink = true;
              link.setLinkNodeId(dragNodeTag.id);
              const { scale, tx, ty } = nodeTag.canvas.getMatrix();
              const prevAngleStartToR = link.angleStartToR;
              const prevAngleEndToR = link.angleEndToR;

              const lNodePoint = nui.getLNodePointForAdoptedNode({ link, draggingRect });
              link.setAngles({ lNodePoint });

              operationMessages.push(
                nodeTag.operation.messages.setLinkAngles({
                  linkUUID: link.id,
                  angleStartToR: link.angleStartToR,
                  angleEndToR: link.angleEndToR,
                  prevAngleStartToR,
                  prevAngleEndToR,
                })
              );
              link.placeLinkNode();
              updateDisplayProperties(dragNodeTag.id,nodeTag.canvas);
              operationMessages.push(
                nodeTag.operation.messages.setLinkNodeId({
                  link,
                  prevLink,
                })
              );
            }
            nui.unsetTransparent({ nodeIds: selectedNodes });
          }

          const newHome = dragdrop.newHome;
          const newParent = store.getNode(dragdrop.newHome.parentId);

          nodeTag.canvas.clearHoveredNode();
          nodeTag.canvas.clearHoveredLink();

          let targetIsPartOfLink = false;
          if (dragNodeTag.getLinkId()) targetIsPartOfLink = true;

          if (!preventAdoption && newParent && newParent.id !== dragNodeTag.id && !targetIsPartOfLink && !targetIsLink) {
            const adopt = (node, newParentId, newPosition, newOrderIndex) => {
              node.setPosition(newPosition);
              node.setOrderIndex(newOrderIndex);
              node.adoptParent(newParentId);
              adoptedCardCount++;
            };

            let newParentId = newParent.id;
            let newPosition = { x: 0, y: 0 };
            let newOrderIndex = 0;

            selectedNodes.push(newParentId);

            if (newParent.getLayoutName() === 'layout-freehand') {

              ndui.handleFreehandAdoption({ adopt, newParent, dragNodeTag, dragInstance });

            } else {
              // new order index upper and lower bounds
              let newOrderIndexUpper = 0;
              let newOrderIndexLower = 1;
              const children = newParent.children();

              if (newHome.first) {
                newOrderIndexUpper = children.length ? (children[0].orderIndex - 1)
                                                     : 0;
                newOrderIndexLower = newOrderIndexUpper - 1;
              } else if (newHome.last || newParent.getIsCollapsed()) {
                newOrderIndexLower = children.length ? (children[children.length - 1].orderIndex + 1)
                                                     : 0;
                newOrderIndexUpper = newOrderIndexLower + 1;
              } else if (newHome.beforeId) {
                const upperIdx = children.findIndex(c => { return c.id == newHome.beforeId; });
                newOrderIndexUpper = children[upperIdx].orderIndex;
                const lowerIdx = children[upperIdx - 1];
                newOrderIndexLower = children[upperIdx - 1] ? children[upperIdx - 1].orderIndex
                                                            : newOrderIndexUpper - 1;
              } else if (newHome.afterId) {
                const lowerIdx = children.findIndex(c => { return c.id == newHome.afterId;});
                newOrderIndexLower = children[lowerIdx].orderIndex;
                newOrderIndexUpper = children[lowerIdx + 1] ? children[lowerIdx + 1].orderIndex
                                                            : newOrderIndexLower + 1;
              } else {
                throw('invalid new home: ', newHome);
              }

              const topLevelNodes = nodeTag.canvas.getRootSelectedNodes();
              if (topLevelNodes.length > 1) {
                const topLevelSort = (n1, n2) => {
                  if (n1.orderIndex === undefined && n2.orderIndex === undefined) {
                    return n1.absolutePosition.y < n2.absolutePosition.y ? -1 : 1;
                  }
                  if (n1.orderIndex === undefined) return -1;
                  if (n2.orderIndex === undefined) return 1;
                  return n1.orderIndex < n2.orderIndex ? -1 : 1;
                };
                topLevelNodes.sort(topLevelSort).forEach((selectedNode, idx) => {
                  newOrderIndex = newOrderIndexLower + (idx + 0.5)/(topLevelNodes.length) * (newOrderIndexUpper - newOrderIndexLower);
                  adopt(selectedNode, newParentId, newPosition, newOrderIndex);
                });
              } else {
                newOrderIndex = (newOrderIndexLower + newOrderIndexUpper) / 2;
                adopt(dragInstance, newParentId, newPosition, newOrderIndex);
              }
            }

            nodeTag.canvas.onboardingPopupTag && canvas.onboardingPopupTag.completeStep('drag-drop');
          }

          $('.undraggable').removeClass('undraggable');

          // take any cards that are off the left or top edge of the freehand container and push them back in
          nui.fitCardsIntoFreehand({ nodeTags: topSelectedElements });
          nui.fitCardsIntoFreehand({ nodeTags: [ nodeTag.canvas.nodeTags[dragNodeTag.id] ] });

          updateSelectedNodeTree(selectedNodes,nodeTag.canvas);
          // resize any nested freehand containers we're inside of here
          refreshFreehandAncestorsOfList(selectedNodes,nodeTag.canvas,store);
          dragNodeTag.topLevelParentTag().rediscoverLinkPositions();
          const nodePositions = store.getNodePositions(targetNodeIds);

          let allowedDirections;
          if (adoptedCardCount){
            /* expand down or right if we're adopting */
            allowedDirections = ['right','down'];
          }
          const overlapTag = nodeTag.canvas.nodeTags[dragNodeTag.id];
          const dragOverlaps = nui.fixOverlaps({ nodeTag: overlapTag, allowedDirections }) || {};

          _reclipLink(draggingRect, dragNodeTag);

          regionUI.applyRegionPositionOperations({ operationMessages });

          operationMessages.unshift(
            nodeTag.operation.messages.updateNodePositions({
              prevNodePositions: prevNodePositions.concat(dragOverlaps.prevNodePositions),
              nodePositions: nodePositions.concat(dragOverlaps.nodePositions),
              prevLinkAngles,
              linkAngles: store.getLinkAngles(targetLinkIds),
              selectedNodes,
            })
          );
          if (dragStartOperations.length || operationMessages.length) {
            nodeTag.operation.batchOperation({
              messages: dragStartOperations.concat(operationMessages),
              onFailure: (err) => {
                console.log(`Node drag end batchOperation failure: ${err}`);
              },
            });
          }

          nodeTag.canvas.setValidFreehandDropTargets([]);
          if (adoptedCardCount && newParent) {
            dragNodeTag.canvas.showCollapsedCardAdoptionPopover({parentId: newParent.id, count: adoptedCardCount});
          }
        } catch(e) {
          if (e instanceof IllegalMapOperationError) {
              // just swallow the error
          } else {
            throw e;
          }
        }
        dragInstance = null;
        dragNodeTag = null;
        dragStartOperations = [];
        nodeTag.canvas.multiSelectionIndicatorBox.update();
      }
    });
};



// WEBPACK FOOTER //
// ./starling/src/lib/node-drag-handlers.js